// ═══════════════════════════════════════════════════════════════════════════
// diaLocal — la regla de "de qué día es esta fecha", en un solo lugar
// ═══════════════════════════════════════════════════════════════════════════
//
// POR QUÉ EXISTE (report chOxlcbjNYlOySOC3BKQ)
// ────────────────────────────────────────────
// El par «clave de día ↔ parseo de la clave» estaba copiado a mano en siete
// lugares del front, y en cuatro de ellos las dos mitades no usaban la misma
// pared: la clave se armaba con `toISOString()` (día UTC) y la grilla —o la
// etiqueta— con los getters locales. El resultado no es un crash sino algo
// peor de encontrar: **el eje X de dos charts de plata mostraba el día
// equivocado**, y el error es de UN día, así que se lee como ruido y no como
// falla. Medido, no razonado: en `America/Argentina/Buenos_Aires` una venta de
// las 22:30 aparecía al día siguiente; en `Europe/Berlin` y `Asia/Tokyo` la
// clave misma quedaba corrida un día.
//
// Seis copias de la regla de qué día es una venta es exactamente como se llega
// a que dos pantallas digan cosas distintas del mismo dato.
//
// ⚠️ PARED — el contrato, y la parte que hay que leer antes de tocar esto
// ─────────────────────────────────────────────────────────────────────────
// Todo acá agrupa en **hora LOCAL del browser** (`getFullYear/getMonth/
// getDate`), nunca en UTC. Dos consecuencias:
//
//   1. **Nunca uses `new Date('YYYY-MM-DD')` para volver de la clave a un
//      Date.** Ese literal date-only lo parsea JS como medianoche UTC, así que
//      en cualquier huso negativo retrocede un día — que es, textual, el bug
//      que este módulo vino a cerrar. Para eso está `parseClaveDiaLocal`.
//   2. La pared del browser NO es la misma que la del rollup `__pulso`, que
//      usa **pared AR fija (UTC-3, sin DST)** por decisión escrita
//      (`docs/CONTRATO_senales-pulso.md:20`,
//      `functions/dashboardRollups/pulsoRollupCore.js:18-25`). Esa pared AR es
//      la aproximación server-side de la pared del browser, no un canon que
//      compita: para un usuario en AR coinciden, para uno mirando desde otro
//      huso no. Es una divergencia declarada, no un descuido.
//
// La pregunta "¿un usuario mirando desde España debe ver los días como los ve
// él, o como los ve la operación en Argentina?" es de producto y sigue
// abierta. Si algún día se responde "pared AR para todo el front", el cambio
// entra ACÁ y no en catorce lugares — que es medio motivo de que esto exista.
// Trigger para reabrirla: primer usuario operando de forma habitual fuera de
// AR.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Clave 'YYYY-MM-DD' del día calendario de `date`, en pared LOCAL.
 *
 * Se arma con los getters locales a propósito: `toISOString().slice(0,10)`
 * daría el día UTC, que para un huso negativo es el mismo día sólo hasta las
 * 21:00 y para uno positivo ya está corrido desde la medianoche.
 *
 * @param {Date} date
 * @returns {string} 'YYYY-MM-DD', o '' si la fecha no es válida
 */
export function claveDiaLocal(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Clave 'YYYY-MM-DD' del LUNES de la semana de `date`, en pared LOCAL.
 * Semana ISO: lunes a domingo (domingo pertenece a la semana que arranca el
 * lunes anterior, no a la siguiente).
 *
 * @param {Date} date
 * @returns {string} 'YYYY-MM-DD' del lunes, o '' si la fecha no es válida
 */
export function claveSemanaLocal(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return ''
  const x = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dia = x.getDay()            // 0 = domingo
  const aLunes = dia === 0 ? -6 : 1 - dia
  x.setDate(x.getDate() + aLunes)
  return claveDiaLocal(x)
}

/**
 * Clave 'YYYY-MM' del mes de `date`, en pared LOCAL.
 *
 * @param {Date} date
 * @returns {string} 'YYYY-MM', o '' si la fecha no es válida
 */
export function claveMesLocal(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Vuelve de una clave 'YYYY-MM-DD' (o 'YYYY-MM') al `Date` de su medianoche
 * LOCAL — el inverso exacto de `claveDiaLocal` / `claveMesLocal`.
 *
 * El `T00:00:00` no es decorativo: es lo único que separa "medianoche local"
 * de "medianoche UTC" en el parser de JS.
 *
 * @param {string} clave — 'YYYY-MM-DD' o 'YYYY-MM'
 * @returns {Date|null} medianoche local, o null si la clave no tiene forma
 */
export function parseClaveDiaLocal(clave) {
  if (typeof clave !== 'string') return null
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(clave.trim())
  if (!m) return null
  const anio = Number(m[1])
  const mes = Number(m[2]) - 1
  const dia = m[3] ? Number(m[3]) : 1
  const d = new Date(anio, mes, dia)
  if (isNaN(d.getTime())) return null
  // ⚠️ El constructor de Date NO valida: `new Date(2026, 12, 45)` no es
  // `Invalid Date`, es el 14-feb-2027 — desborda en silencio. Sin este
  // chequeo, `'2026-13-45'` devolvía una fecha PLAUSIBLE y equivocada, que es
  // peor que null: un filtro por día construido con eso consulta otro mes y la
  // pantalla dice "no hay nada", no "la fecha estaba mal".
  // (Encontrado escribiendo `rangoDelDiaLocal`, report jQGkD93uR9EhjlD0mr7s.)
  if (d.getFullYear() !== anio || d.getMonth() !== mes || d.getDate() !== dia) return null
  return d
}

/**
 * El rango `[inicio, fin)` de un día calendario LOCAL, listo para un filtro de
 * Firestore (`where('campo','>=',inicio)` + `where('campo','<',fin)`).
 *
 * Vive acá y no en el módulo que lo pide (report jQGkD93uR9EhjlD0mr7s) porque
 * es la MISMA pared que el resto del archivo, y el par «clave → rango» ya
 * estaba copiado a mano en el front: `ClientLogsTab.jsx:112-113` lo arma con
 * `T00:00:00`/`T23:59:59`, que además pierde el último segundo del día. Una
 * segunda copia de la regla de husos es cómo se llegó a este archivo.
 *
 * Dos decisiones que el `+ 86400000` obvio se lleva puestas:
 *
 *   1. **Fin EXCLUSIVO**, no `23:59:59`: entre las 23:59:59.001 y la
 *      medianoche hay jobs, y con el fin inclusivo no existen.
 *   2. El fin se construye con el **constructor por componentes**
 *      (`new Date(y, m, d + 1)`), no sumando 24 h en milisegundos. En un huso
 *      con DST el día dura 23 o 25 horas, y sumar 86.400.000 deja el corte una
 *      hora adentro del día siguiente (o una hora antes del final). Hoy AR no
 *      tiene DST, así que ese mutante NO se muere probando sólo en AR — el
 *      test lo prueba en un huso que sí lo tiene.
 *
 * @param {string} clave — 'YYYY-MM-DD' (lo que devuelve un `<input type="date">`)
 * @returns {{inicio: Date, fin: Date}|null} `null` si la clave no tiene forma
 */
export function rangoDelDiaLocal(clave) {
  const inicio = parseClaveDiaLocal(clave)
  if (!inicio) return null
  const fin = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + 1)
  return { inicio, fin }
}

/**
 * El inicio de una ventana de ventas de `dias` días: la medianoche LOCAL del
 * browser de hace `dias` días calendario. Es la pared de este módulo (arriba).
 *
 * Fuente única del «desde» de las lecturas de órdenes con ventana (report
 * djynjlXICuUF0Yl803M8): el loader de los agentes (`agentes.js`) y la vista de
 * detección que tiene que traer EXACTAMENTE la misma ventana (paridad) lo
 * calculaban cada uno con su `setDate`/`setHours`. Dos copias del mismo
 * cálculo de fecha es la clase dia-utc-vs-pared-local: el día que una pase a
 * UTC, la otra no se entera.
 *
 * @param {number} dias
 * @param {Date} [ahora]
 * @returns {Date}
 */
export function inicioVentanaLocal(dias, ahora = new Date()) {
  return new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() - dias)
}

const ZONA_AR = 'America/Argentina/Buenos_Aires'
const PARTES_AR = new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_AR, year: 'numeric', month: '2-digit', day: '2-digit' })

/**
 * La OTRA pared, la de AR (UTC-3 fija): la de los datos que el server fecha en
 * hora argentina (`fechaCorrida` de los rollups diarios, `fecha` de
 * aiUsageDaily). Para comparar contra esas claves no se cruza a la pared del
 * browser: se arma la clave en AR y se compara clave contra clave.
 *
 * Clave 'YYYY-MM-DD' del día AR de `ahora`, corrida `offsetDias` días hacia
 * atrás. La resta es de días CALENDARIO (componentes en UTC, sin DST), no de
 * 86.400.000 ms.
 *
 * @param {Date} [ahora]
 * @param {number} [offsetDias]
 * @returns {string}
 */
export function claveDiaAR(ahora = new Date(), offsetDias = 0) {
  const p = Object.fromEntries(PARTES_AR.formatToParts(ahora).map((x) => [x.type, x.value]))
  const d = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) - offsetDias))
  return d.toISOString().slice(0, 10)
}

/**
 * El día para MOSTRAR, 'DD/MM/AAAA', en pared AR — un solo formato para las
 * fechas que una pantalla enumera juntas (report f30qpuzO2MF6yJjk4XPL: el
 * renglón de frescura de Mi Posición mezclaba claves ISO crudas con el
 * `toLocaleDateString` del browser en la misma oración).
 *
 * Una clave 'YYYY-MM-DD' (la de un rollup, ya en pared AR) se reordena sin
 * pasar por `new Date(...)`, que la correría un día (cabecera, punto 1). Un
 * `Date` o un número de ms se lleva a su día AR con `claveDiaAR`.
 *
 * No es `diaMes(x, { conAnio: true })` (`corteDeDatos.js`): ése lee la pared
 * del browser, y con una clave ISO pasada por `new Date` se corre un día.
 *
 * @param {string|Date|number} valor
 * @returns {string|null} 'DD/MM/AAAA', o null si no es una fecha
 */
export function diaAR(valor) {
  let clave = null
  if (typeof valor === 'string') clave = /^\d{4}-\d{2}-\d{2}$/.test(valor) ? valor : null
  else if (valor instanceof Date || typeof valor === 'number') {
    const fecha = valor instanceof Date ? valor : new Date(valor)
    clave = Number.isFinite(fecha.getTime()) ? claveDiaAR(fecha) : null
  }
  return clave && `${clave.slice(8, 10)}/${clave.slice(5, 7)}/${clave.slice(0, 4)}`
}
